import { call, put, takeLatest, CallEffect, PutEffect } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import toast from 'react-hot-toast';
import axios from 'axios';
import client from '../../service/graphql';
import {
  ADD_TEXT_NOTE_FOR_CLIENT,
  EDIT_TEXT_NOTE_BLOCK,
  GET_NOTES_FOR_CLIENT_LAST_NOTE,
  GET_PRE_SIGNED_URL,
  DELETE_TEXT_NOTE_FOR_CLIENT,
  SAVE_NOTE_RECORDING,
  GET_NOTE_RECORDINGS_FOR_CLIENT,
  DELETE_RECORDING_FOR_CLIENT,
  GET_NOTE_SUGGESTIONS,
} from '../../service/graphql/noteQuery';
import {
  fetchNotesRequest,
  fetchNotesSuccess,
  fetchNotesFailure,
  addNoteFailure,
  addTextNoteRequest,
  getPresignedUrlRequest,
  getPresignedUrlSuccess,
  getPresignedUrlFailure,
  editSingleNoteRequest,
  editNoteFailure,
  addNoteSuccess,
  editNoteSuccess,
  deleteSingleNoteRequest,
  deleteNoteSuccess,
  saveNoteRecording,
  ISaveNoteRecordingPayload,
  saveNoteRecordingSuccess,
  saveNoteRecordingFailure,
  getNoteRecordings,
  IGetNoteRecordings,
  getNoteRecordingsSuccess,
  getNoteRecordingsFailure,
  deleteNoteRecordingRequest,
  deleteNoteRecordingSuccess,
  deleteNoteRecordingFailure,
  getNoteSuggestionsSuccess,
  getNoteSuggestionsFailure,
  getNoteSuggestionsRequest,
  IGetNoteSuggestions,
} from './noteSlice';
import { IGraphQLResponse } from '../../types/commonTypes';
import {
  IAddTextNoteForClientResponse,
  ICreateNote,
  ICreateNoteClientResponse,
  IEditNoteClientResponse,
  IGetNoteRecordingsForClientResponse,
  IGetNotesForClientResponse,
  IGetNotesPayload,
  ISaveNoteRecordingResponse,
} from '../../types/notes';
import i18n from '../../i18n';
import { toastStyles } from '../../utils/utils';
import { GraphQLErrorTypes, getGraphQLError } from '../../utils/errorMap';

async function saveprofilePicInS3({
  fileObject,
  preSignedUrl,
}: {
  fileObject: File | null;
  preSignedUrl: string;
}): Promise<void> {
  if (fileObject?.name) {
    await axios.put(preSignedUrl, fileObject, {
      headers: {
        'Content-Type': `${fileObject.type}`,
      },
    });
  }
}

function* watchFetchNotesForClient({
  payload,
}: PayloadAction<IGetNotesPayload>): Generator<
  CallEffect | PutEffect,
  void,
  IGraphQLResponse<IGetNotesForClientResponse>
> {
  const { clientId, coachId, page, pageSize } = payload;
  try {
    const result: IGraphQLResponse<IGetNotesForClientResponse> = yield call(
      client.query.bind(client),
      {
        query: GET_NOTES_FOR_CLIENT_LAST_NOTE,
        variables: { coachId, clientId, page, pageSize, orderBy: 'createdAt', order: 'desc' },
        fetchPolicy: 'cache-first',
      }
    );
    yield put(fetchNotesSuccess(result.data.getNotes));
  } catch (error: unknown) {
    if (error instanceof Error) yield put(fetchNotesFailure(error.message));
  }
}

function* watchAddTextNoteForClient({
  payload,
}: PayloadAction<{
  tempId: string;
  clientId: string;
  coachId: string;
  text: string;
  html: string;
  transcription?: string;
  contentType?: string;
  file?: File;
  recording?: string;
}>) {
  const { clientId, coachId, text, html, tempId, transcription, contentType, file, recording } =
    payload;

  try {
    const result: IGraphQLResponse<ICreateNoteClientResponse> = yield call(
      client.mutate.bind(client),
      {
        mutation: ADD_TEXT_NOTE_FOR_CLIENT,
        variables: {
          coachId,
          clientId,
          text,
          html,
        },
      }
    );
    const createdNote: ICreateNote = result.data.addNote;
    createdNote.tempId = tempId;
    yield put(addNoteSuccess(createdNote));
    if (transcription && contentType && file && recording) {
      yield put(
        saveNoteRecording({
          clientId,
          coachId,
          noteId: createdNote.id,
          recording,
          transcription,
          contentType,
          file,
          suggestions: [],
        })
      );
    }
    client.resetStore();
  } catch (error: unknown) {
    if (error instanceof Error) yield put(addNoteFailure(error.message));
  }
}

function* watchAddMediaNoteForClient({
  payload,
}: PayloadAction<{
  clientId: string;
  fileName: string;
  contentType: string;
  fileObject: File | null;
}>): Generator<CallEffect | PutEffect, void, IGraphQLResponse<IAddTextNoteForClientResponse>> {
  const { clientId, fileName, contentType, fileObject } = payload;
  try {
    // First, get the pre-signed URL
    const urlResponse: IGraphQLResponse<IAddTextNoteForClientResponse> = yield call(client.query, {
      query: GET_PRE_SIGNED_URL,
      variables: {
        clientId,
        fileName,
        contentType,
      },
    });

    const preSignedUrl = urlResponse.data.getNotePicutureUploadURL.url;
    const { key } = urlResponse.data.getNotePicutureUploadURL;

    const payloadObj = {
      fileObject,
      preSignedUrl,
    };

    const data = {
      preSignedUrl,
      key,
    };

    yield call(saveprofilePicInS3, payloadObj);
    yield put(getPresignedUrlSuccess(data));
  } catch (error: unknown) {
    if (error instanceof Error) yield put(getPresignedUrlFailure(error.message));
  }
}

function* watchEditNoteForClient({
  payload,
}: PayloadAction<{
  clientId: string;
  coachId: string;
  noteId: string;
  text: string;
  isForceSuggestions: boolean;
  html: string;
}>) {
  const { clientId, coachId, noteId, text, isForceSuggestions, html } = payload;
  try {
    const result: IGraphQLResponse<IEditNoteClientResponse> = yield call(
      client.mutate.bind(client),
      {
        mutation: EDIT_TEXT_NOTE_BLOCK,
        variables: {
          coachId,
          clientId,
          noteId,
          text,
          isForceSuggestions,
          html,
        },
      }
    );
    yield put(editNoteSuccess(result.data.editNote));
  } catch (error: unknown) {
    if (error instanceof Error) yield put(editNoteFailure(error.message));
  }
}

function* watchDeleteNoteForClient({
  payload,
}: PayloadAction<{
  id: string;
  clientId: string;
  coachId: string;
}>): Generator<CallEffect | PutEffect, void, unknown> {
  const { id } = payload;
  try {
    yield call(client.mutate.bind(client), {
      mutation: DELETE_TEXT_NOTE_FOR_CLIENT,
      variables: {
        id,
      },
    });
    yield put(deleteNoteSuccess(id));
    client.resetStore();
    toast.success(i18n.t('notes.noteDeleted'), toastStyles.error);
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(editNoteFailure(error.message));
      const extractedError = getGraphQLError(error);
      if (extractedError) {
        if (extractedError.code === GraphQLErrorTypes.FORBIDDEN) {
          toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
        }
      } else {
        toast.error(i18n.t('notes.noteNotDeleted'), toastStyles.error);
      }
    }
  }
}

function* watchSaveNoteRecording({ payload }: PayloadAction<ISaveNoteRecordingPayload>) {
  const { clientId, coachId, noteId, recording, transcription, contentType, file, suggestions } =
    payload;

  try {
    const result: IGraphQLResponse<ISaveNoteRecordingResponse> = yield call(
      client.mutate.bind(client),
      {
        mutation: SAVE_NOTE_RECORDING,
        variables: {
          clientId,
          coachId,
          noteId,
          recording,
          transcription,
          contentType,
          suggestions,
        },
      }
    );
    const payloadObj = {
      fileObject: file,
      preSignedUrl: result.data.saveNoteRecording.presignedUrl,
    };

    yield call(saveprofilePicInS3, payloadObj);
    yield put(saveNoteRecordingSuccess(result.data.saveNoteRecording));
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(saveNoteRecordingFailure());
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(error.message, toastStyles.error);
      }
    }
  }
}

function* watchFetchNoteRecordingsForClient({
  payload,
}: PayloadAction<IGetNoteRecordings>): Generator<
  CallEffect | PutEffect,
  void,
  IGraphQLResponse<IGetNoteRecordingsForClientResponse>
> {
  const { clientId, coachId, noteId } = payload;
  try {
    const result: IGraphQLResponse<IGetNoteRecordingsForClientResponse> = yield call(
      client.query.bind(client),
      {
        query: GET_NOTE_RECORDINGS_FOR_CLIENT,
        variables: { coachId, clientId, noteId },
      }
    );
    yield put(getNoteRecordingsSuccess(result.data.getNoteRecordings));
  } catch (error: unknown) {
    if (error instanceof Error) yield put(getNoteRecordingsFailure());
  }
}

function* watchDeleteRecordingForClient({
  payload,
}: PayloadAction<{
  recordingId: string;
  clientId: string;
  coachId: string;
}>): Generator<CallEffect | PutEffect, void, unknown> {
  try {
    yield call(client.mutate.bind(client), {
      mutation: DELETE_RECORDING_FOR_CLIENT,
      variables: {
        ...payload,
      },
    });
    yield put(deleteNoteRecordingSuccess(payload.recordingId));
    toast.success(i18n.t('notes.recordingDeleted'), toastStyles.error);
  } catch (error: unknown) {
    yield put(deleteNoteRecordingFailure());
    const extractedError = getGraphQLError(error);
    if (extractedError) {
      if (extractedError.code === GraphQLErrorTypes.FORBIDDEN) {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      }
    } else {
      toast.error(i18n.t('notes.noteNotDeleted'), toastStyles.error);
    }
  }
}

function* watchFetchNoteSuggestions({ payload }: PayloadAction<IGetNoteRecordings>) {
  const { clientId, coachId, noteId } = payload;
  try {
    const result: IGraphQLResponse<IGetNoteSuggestions> = yield call(client.query.bind(client), {
      query: GET_NOTE_SUGGESTIONS,
      variables: {
        getNoteSuggestionsInput: {
          clientId,
          coachId,
          noteId,
        },
      },
    });

    yield put(getNoteSuggestionsSuccess(result.data));
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(getNoteSuggestionsFailure(error.message));
    }
  }
}

export function* watchNoteSagas() {
  yield takeLatest(fetchNotesRequest, watchFetchNotesForClient);
  yield takeLatest(addTextNoteRequest, watchAddTextNoteForClient);
  yield takeLatest(getPresignedUrlRequest, watchAddMediaNoteForClient);
  yield takeLatest(editSingleNoteRequest, watchEditNoteForClient);
  yield takeLatest(deleteSingleNoteRequest, watchDeleteNoteForClient);
  yield takeLatest(saveNoteRecording, watchSaveNoteRecording);
  yield takeLatest(getNoteRecordings, watchFetchNoteRecordingsForClient);
  yield takeLatest(deleteNoteRecordingRequest, watchDeleteRecordingForClient);
  yield takeLatest(getNoteSuggestionsRequest, watchFetchNoteSuggestions);
}

const notesSagas = watchNoteSagas;
export default notesSagas;
